import React from 'react';
import type { IconType } from 'react-icons';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'outline';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  icon?: IconType;
  iconPlacement?: 'left' | 'right';
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      icon: Icon,
      iconPlacement = 'left',
      isLoading,
      children,
      ...props
    },
    ref,
  ) => {
    const variants = {
      primary:
        'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-900/20 active:scale-95 transition-all duration-200',
      secondary:
        'bg-zinc-800 text-white hover:bg-zinc-700 active:scale-95 transition-all duration-200',
      ghost:
        'bg-transparent text-gray-400 hover:text-white hover:bg-white/10 transition-all duration-200',
      outline:
        'bg-transparent border border-zinc-700 text-white hover:bg-white/5 transition-all duration-200',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-xs rounded-md font-medium',
      md: 'px-4 py-2 text-sm rounded-lg font-semibold',
      lg: 'px-6 py-3 text-base rounded-xl font-bold',
      icon: 'p-2 rounded-full',
    };

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed',
          variants[variant],
          sizes[size],
          className,
        )}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading && (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        )}
        {!isLoading && Icon && iconPlacement === 'left' && <Icon size={size === 'sm' ? 14 : 18} />}
        {children}
        {!isLoading && Icon && iconPlacement === 'right' && <Icon size={size === 'sm' ? 14 : 18} />}
      </button>
    );
  },
);

Button.displayName = 'Button';
